/*
 * Pure logic for the Advisors tab: query building, deep link, labels, and the
 * preview -> commit batching. No I/O; the panel and api.ts are thin around this.
 */

import moment from 'moment-timezone';
import type { ComboUser } from '../../../util_components/user_combobox/user_match';
import type {
  AdvisorRow,
  EndRequest,
  EndResult,
  AdvisorSummary,
  AdvisorLevel,
  AdvisorSource,
  AssignRequest,
  AssignResult,
  Commit,
  CommitRequest,
  HistoryRow,
  Preview,
  PreviewRequest
} from './types';

const IST_TZ = 'Asia/Kolkata';

// Server cap on commit items per request (G3 commit contract).
export const MAX_COMMIT_ITEMS = 500;
export const PAGE_LIMIT = 50;
export const REASSIGN_ACTION = 'REASSIGN';
export const ADVISOR_PARAM = 'advisor';

// Server cap on a by-advisor preview (G3 preview contract).
export const PREVIEW_CAP = 500;

export const LEVELS: AdvisorLevel[] = ['L1', 'L2', 'L3', 'MANAGER'];
export const SOURCES: AdvisorSource[] = ['FIRST_1ON1_NOTE', 'REASSIGN', 'MANUAL_ASSIGN'];

// "Reassignment" (not "Reassigned") so it never reads like the REASSIGNED end reason.
export const SOURCE_LABELS: Record<string, string> = {
  FIRST_1ON1_NOTE: 'First 1-on-1 note',
  REASSIGN: 'Reassignment',
  MANUAL_ASSIGN: 'Manual assignment'
};

export const END_REASON_LABELS: Record<string, string> = {
  EXPIRED_35D: 'Expired (35 days)',
  REASSIGNED: 'Reassigned',
  ACCOUNT_DELETED: 'Account deleted',
  BANNED: 'Student banned',
  NO_SHOW: 'Student no-show (auto-ended)',
  MANUAL_END: 'Ended manually'
};

export const SKIP_REASON_LABELS: Record<string, string> = {
  no_active_advisor: 'No active advisor',
  same_advisor: 'Already with this advisor',
  source_not_active: 'Assignment is no longer active',
  target_ineligible: 'Target advisor is not eligible'
};

export const OUTCOME_LABELS: Record<string, string> = {
  reassigned: 'Reassigned',
  duplicate: 'Already done (batch retried)',
  skipped: 'Skipped'
};

// Unknown server codes are shown raw rather than hidden.
const label = (map: Record<string, string>, code: string | null | undefined): string =>
  code ? map[code] || code : '';

export const sourceLabel = (code: string): string => label(SOURCE_LABELS, code);
export const endReasonLabel = (code: string): string => label(END_REASON_LABELS, code);
export const skipReasonLabel = (code: string): string => label(SKIP_REASON_LABELS, code);
export const outcomeLabel = (code: string): string => label(OUTCOME_LABELS, code);

// RFC3339 -> IST wall clock; the sales team works in IST.
export const formatTs = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const m = moment.utc(iso, moment.ISO_8601, true);
  return m.isValid() ? `${m.tz(IST_TZ).format('D MMM YYYY, HH:mm')} IST` : iso;
};

// Date only (IST) for narrow table cells; formatTs goes in the title.
export const formatDay = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const m = moment.utc(iso, moment.ISO_8601, true);
  return m.isValid() ? m.tz(IST_TZ).format('D MMM YYYY') : iso;
};

// IST wall-clock time only, the second line under formatDay; '' when there is no valid instant.
export const formatTime = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const m = moment.utc(iso, moment.ISO_8601, true);
  return m.isValid() ? `${m.tz(IST_TZ).format('HH:mm')} IST` : '';
};

const DAY_MS = 24 * 60 * 60 * 1000;

// Whole days until expiry, rounded up (<= 0 means expired); null when unknown.
export const daysLeft = (expiresAt: string | null | undefined, now: number): number | null => {
  if (!expiresAt) return null;
  const m = moment.utc(expiresAt, moment.ISO_8601, true);
  if (!m.isValid()) return null;
  const d = Math.ceil((m.valueOf() - now) / DAY_MS);
  return d === 0 ? 0 : d; // no -0
};

export interface Filters {
  advisor: string;
  level: AdvisorLevel | '';
  expiringWithinDays: string;
  source: AdvisorSource | '';
  q: string;
}

export const EMPTY_FILTERS: Filters = {
  advisor: '',
  level: '',
  expiringWithinDays: '',
  source: '',
  q: ''
};

const positiveInt = (v: string): string => {
  const n = Number(v);
  return Number.isInteger(n) && n > 0 ? String(n) : '';
};

// Any filter beyond the advisor narrows the list (picks the empty-state copy).
export const hasNarrowingFilters = (f: Filters): boolean =>
  !!(f.level || f.source || positiveInt(f.expiringWithinDays) || f.q.trim());

// Empty filters are omitted so the server applies its defaults.
export const listQuery = (f: Filters, cursor = 0, limit = PAGE_LIMIT): string => {
  const p = new URLSearchParams();
  if (f.advisor) p.set('advisor', f.advisor);
  if (f.level) p.set('level', f.level);
  const days = positiveInt(f.expiringWithinDays);
  if (days) p.set('expiring_within_days', days);
  if (f.source) p.set('source', f.source);
  const q = f.q.trim();
  if (q) p.set('q', q);
  if (cursor > 0) p.set('cursor', String(cursor));
  p.set('limit', String(limit));
  return `?${p.toString()}`;
};

// Old deep link (goserver still sends it): /sales-task-list?tab=advisors&advisor=<cms uuid>
export const advisorFromSearch = (search: string | URLSearchParams): string =>
  (typeof search === 'string' ? new URLSearchParams(search) : search).get(ADVISOR_PARAM) || '';

// The advisor students page; the old ?advisor= deep link is replaced onto it.
export const ADVISORS_TAB_PATH = '/sales-task-list?tab=advisors';
export const advisorPagePath = (uuid: string): string =>
  `/sales-task-list/advisors/${encodeURIComponent(uuid)}`;

// Same rule the server enforces on the target: active roster member holding SALES.
export const eligibleTargets = (summary: AdvisorSummary[]): ComboUser[] =>
  summary
    .filter((s) => s.roster_status === 'ACTIVE' && s.has_sales)
    .map((s) => ({ uuid: s.cms_user_uuid, name: s.level ? `${s.name} (${s.level})` : s.name }));

export const advisorOptions = (summary: AdvisorSummary[]): ComboUser[] =>
  summary.map((s) => ({ uuid: s.cms_user_uuid, name: s.name }));

// Why a summary row is flagged: roster LEFT and/or SALES role missing.
export const flagReasons = (s: AdvisorSummary): string[] => {
  const out: string[] = [];
  if (s.roster_status === 'LEFT') out.push('Left the roster');
  if (!s.has_sales) out.push('No SALES role');
  return out;
};

export const previewByStudents = (studentUuids: string[], to: string): PreviewRequest => ({
  student_uuids: studentUuids,
  to_cms_user_uuid: to
});

export const previewByAdvisor = (from: string, to: string): PreviewRequest => ({
  from_cms_user_uuid: from,
  to_cms_user_uuid: to
});

// Only REASSIGN items are committed; anything else (skips) never reaches commit.
export const commitRequests = (
  preview: Preview,
  to: string,
  maxItems = MAX_COMMIT_ITEMS
): CommitRequest[] => {
  const items = preview.items
    .filter((i) => i.action === REASSIGN_ACTION)
    .map((i) => ({ student_uuid: i.student_uuid, row_id: i.row_id }));
  const out: CommitRequest[] = [];
  for (let i = 0; i < items.length; i += maxItems) {
    out.push({
      batch_ref: preview.batch_ref,
      to_cms_user_uuid: to,
      items: items.slice(i, i + maxItems)
    });
  }
  return out;
};

export const mergeCommits = (batchRef: string, parts: Commit[]): Commit => ({
  batch_ref: batchRef,
  results: parts.flatMap((p) => p.results),
  counts: parts.reduce(
    (acc, p) => ({
      reassigned: acc.reassigned + (p.counts?.reassigned || 0),
      duplicate: acc.duplicate + (p.counts?.duplicate || 0),
      skipped: acc.skipped + (p.counts?.skipped || 0)
    }),
    { reassigned: 0, duplicate: 0, skipped: 0 }
  )
});

// Who ended the row, else who assigned it (manual reassign); system rows have neither.
export const historyActor = (h: HistoryRow): string => {
  if (h.ended_by_name) return `Ended by ${h.ended_by_name}`;
  return h.assigned_by_name ? `Assigned by ${h.assigned_by_name}` : '—';
};

// Student names for the result list. Earlier lists win: the preview items are
// captured before the reload drops the moved students from the filtered rows.
export const studentNames = (
  ...lists: { student_uuid: string; student_name: string }[][]
): Map<string, string> => {
  const out = new Map<string, string>();
  lists.forEach((l) =>
    l.forEach((i) => {
      if (!out.has(i.student_uuid)) out.set(i.student_uuid, i.student_name);
    })
  );
  return out;
};

// A by-advisor preview stops at the server cap; the rest needs another preview.
export const previewTruncated = (preview: Preview, byAdvisor: boolean): boolean =>
  byAdvisor && preview.items.length >= PREVIEW_CAP;

// The row can pass expires_at between list load and click; the server then
// answers 200 with end_reason EXPIRED_35D instead of ending it itself.
export const endNotice = (
  row: Pick<AdvisorRow, 'student_name' | 'advisor_name'>,
  res: Pick<EndResult, 'end_reason' | 'ended_at'> | null | undefined
): string =>
  res?.end_reason === 'EXPIRED_35D'
    ? `${row.student_name}'s assignment had already expired at ${formatTs(res.ended_at)}`
    : `Ended ${row.student_name}'s assignment with ${row.advisor_name}`;

export interface CommitRun {
  commit: Commit;
  // Set when a chunk failed; earlier chunks are already committed. Retrying with
  // the same batch_ref reports those students as "duplicate", so it is safe.
  error: unknown;
}

// Chunks run sequentially so a failure leaves a clean "done so far" result.
export const runCommit = async (
  batchRef: string,
  requests: CommitRequest[],
  send: (req: CommitRequest) => Promise<Commit>
): Promise<CommitRun> => {
  const parts: Commit[] = [];
  const step = async (i: number): Promise<unknown> => {
    if (i >= requests.length) return null;
    try {
      parts.push(await send(requests[i]));
    } catch (e) {
      return e ?? new Error('commit failed');
    }
    return step(i + 1);
  };
  const error = await step(0);
  return { commit: mergeCommits(batchRef, parts), error };
};

// ---- Manual "Assign advisor" (REQ 57), CONTRACT G3_assign ----

// Lifetime of a new assignment; the server sets expires_at = now + 35 days.
export const ASSIGN_DAYS = 35;

export const assignRequest = (
  studentUuid: string,
  toCmsUserUuid: string,
  requestRef: string
): AssignRequest => ({
  student_uuid: studentUuid,
  to_cms_user_uuid: toCmsUserUuid,
  request_ref: requestRef
});

export const assignConfirmText = (studentName: string, advisorName: string): string =>
  `Assign ${studentName} to ${advisorName}? The assignment runs ${ASSIGN_DAYS} days from now.`;

// A duplicate means this request_ref was already applied (a retry); no second row exists.
export const assignNotice = (studentName: string, res: AssignResult): string =>
  res.outcome === 'duplicate'
    ? `${studentName} was already assigned to ${res.advisor_name} (request already applied)`
    : `Assigned ${studentName} to ${res.advisor_name}`;

export const alreadyAssignedNotice = (advisorName: string): string =>
  advisorName
    ? `Already assigned to ${advisorName} — use Reassign`
    : 'Already assigned — use Reassign';

// Advisors views: SUPER_USER or a roster member with can_edit_team; the server enforces the real rule.
export const canViewAdvisors = (
  isSuperUser: boolean,
  me: { can_edit_team?: boolean } | null | undefined
): boolean => isSuperUser || !!me?.can_edit_team;

// Advisor writes (assign, reassign, end): /me's can_write_advisors only; SUPER_USER does not grant it.
export const canWriteAdvisors = (
  me: { can_write_advisors?: boolean } | null | undefined
): boolean => !!me?.can_write_advisors;

// ---- Not-assigned (ignored tags) sub-view, CONTRACT G11/G12 ----

export const VIEW_PARAM = 'view';
export const IGNORED_VIEW = 'ignored';
export const IGNORED_PAGE_LIMIT = 50;
const DAY_FMT = 'YYYY-MM-DD';
const DEFAULT_RANGE_DAYS = 7;

export const IGNORED_REASON_ORDER = [
  'ignored_already_assigned',
  'ignored_ineligible_author',
  'ignored_no_started_trial',
  'ignored_window_elapsed'
];

// Copy of goserver db.IgnoredTagLabel (G12 labels.row_level); keep in sync.
export const ignoredRowLabel = (reason: string, detail: string, advisorName: string): string => {
  switch (reason) {
    case 'ignored_ineligible_author':
      if (detail === 'no_sales') return 'no SALES';
      if (detail === 'roster_left') return 'LEFT';
      return 'ineligible author';
    case 'ignored_no_started_trial':
      return 'no started trial';
    case 'ignored_window_elapsed':
      return 'window elapsed';
    case 'ignored_already_assigned':
      return advisorName ? `already assigned to ${advisorName}` : 'already assigned';
    default:
      return reason;
  }
};

// Reason-level label (G12 labels.reason_level), used for the summary chips.
export const ignoredReasonLabel = (reason: string): string =>
  reason === 'ignored_ineligible_author' ? 'ineligible author' : ignoredRowLabel(reason, '', '');

export interface DayRange {
  from: string;
  to: string;
}

// Last 7 IST days ending today (inclusive), the server's default window.
export const defaultIgnoredRange = (now: moment.MomentInput = moment()): DayRange => {
  const to = moment.tz(now, IST_TZ);
  return {
    from: to
      .clone()
      .subtract(DEFAULT_RANGE_DAYS - 1, 'days')
      .format(DAY_FMT),
    to: to.format(DAY_FMT)
  };
};

const validDay = (v: string): boolean => moment(v, DAY_FMT, true).isValid();

// Server rule: both days valid, from <= to, at most 92 days.
export const rangeError = (r: DayRange): string => {
  if (!validDay(r.from) || !validDay(r.to)) return 'Pick a valid from and to date';
  const days = moment(r.to, DAY_FMT).diff(moment(r.from, DAY_FMT), 'days');
  if (days < 0) return 'From date must not be after the to date';
  if (days > 91) return 'Range can be at most 92 days';
  return '';
};

export const ignoredQuery = (r: DayRange, cursor = 0, limit = IGNORED_PAGE_LIMIT): string => {
  const p = new URLSearchParams({ from: r.from, to: r.to });
  if (cursor > 0) p.set('cursor', String(cursor));
  p.set('limit', String(limit));
  return `?${p.toString()}`;
};

const toParams = (search: string | URLSearchParams): URLSearchParams =>
  typeof search === 'string' ? new URLSearchParams(search) : search;

// Deep link: /sales-task-list?tab=advisors&view=ignored&from=D&to=D
export const isIgnoredView = (search: string | URLSearchParams): boolean =>
  toParams(search).get(VIEW_PARAM) === IGNORED_VIEW;

export const rangeFromSearch = (search: string | URLSearchParams): DayRange => {
  const p = toParams(search);
  const r = { from: p.get('from') || '', to: p.get('to') || '' };
  return r.from && r.to && !rangeError(r) ? r : defaultIgnoredRange();
};

export type AdvisorsView = 'assignments' | typeof IGNORED_VIEW;

// view/from/to only mean something on the Not-assigned view; Assignments is the default.
export const searchWithView = (
  search: string | URLSearchParams,
  view: AdvisorsView,
  range: DayRange
): string => {
  const p = new URLSearchParams(toParams(search).toString());
  if (view === IGNORED_VIEW) {
    p.set(VIEW_PARAM, IGNORED_VIEW);
    p.set('from', range.from);
    p.set('to', range.to);
  } else {
    p.delete(VIEW_PARAM);
    p.delete('from');
    p.delete('to');
  }
  const out = p.toString();
  return out ? `?${out}` : '';
};

// Every param this tab owns; they are dropped when the user leaves the tab.
export const ADVISOR_URL_PARAMS = [ADVISOR_PARAM, VIEW_PARAM, 'from', 'to'];

export const endRequest = (r: Pick<AdvisorRow, 'student_uuid' | 'row_id'>): EndRequest => ({
  student_uuid: r.student_uuid,
  row_id: r.row_id
});

// Student profile in Hridaya; opened in a new tab from the advisor tables.
export const studentProfilePath = (studentUuid: string): string =>
  `/studentprofile?id=${encodeURIComponent(studentUuid)}`;
